import React, { useState } from 'react'
import {
  XMarkIcon,
  UserCircleIcon,
  FlagIcon,
  TrashIcon,
  ChatBubbleLeftEllipsisIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
} from '@heroicons/react/24/outline'
import { FlagIcon as FlagSolid, HandThumbUpIcon as HandThumbUpSolid, HandThumbDownIcon as HandThumbDownSolid } from '@heroicons/react/24/solid'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface CommentAuthor {
  _id: string
  displayName: string
  username: string
  avatar?: string
}
export interface CommentReply {
  _id: string
  paragraphIndex: number
  parentCommentId: string
  text: string
  likeCount: number
  dislikeCount: number
  flagCount: number
  likedByMe: boolean
  dislikedByMe: boolean
  flaggedByMe: boolean
  createdAt: number
  author: CommentAuthor | null
}
export interface ParagraphComment {
  _id: string
  paragraphIndex: number
  parentCommentId: null
  text: string
  likeCount: number
  dislikeCount: number
  flagCount: number
  likedByMe: boolean
  dislikedByMe: boolean
  flaggedByMe: boolean
  createdAt: number
  author: CommentAuthor | null
  replies: CommentReply[]
}

// ---------------------------------------------------------------------------
// AddCommentModal
// ---------------------------------------------------------------------------
export const AddCommentModal: React.FC<{
  paragraphIndex: number
  replyTo?: { id: string; authorName: string }
  onSubmit: (text: string, parentCommentId?: string) => Promise<void>
  onClose: () => void
}> = ({ paragraphIndex, replyTo, onSubmit, onClose }) => {
  const [text, setText] = useState('')
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!text.trim()) { setError('Comment cannot be empty'); return }
    setError(null)
    setIsBusy(true)
    try {
      await onSubmit(text.trim(), replyTo?.id)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to post comment')
      setIsBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <ChatBubbleLeftEllipsisIcon className="w-5 h-5 text-primary-500" />
            {replyTo ? `Reply to ${replyTo.authorName}` : `Comment on paragraph ${paragraphIndex + 1}`}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <textarea
          autoFocus
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={replyTo ? `Reply to ${replyTo.authorName}…` : 'Share your thoughts on this paragraph…'}
          className="input w-full resize-none text-sm"
          maxLength={2000}
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">{text.length}/2000</span>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={isBusy || !text.trim()}
              className="px-5 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              {isBusy ? 'Posting…' : replyTo ? 'Reply' : 'Post'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// CommentRow
// ---------------------------------------------------------------------------
export const CommentRow: React.FC<{
  comment: ParagraphComment | CommentReply
  currentUserId?: string
  isAuthor: boolean
  isReply?: boolean
  onDelete: (id: string) => Promise<void>
  onFlag: (id: string) => Promise<void>
  onLike: (id: string) => Promise<void>
  onDislike: (id: string) => Promise<void>
  onReply?: (id: string, authorName: string) => void
  busyId: string | null
}> = ({ comment, currentUserId, isAuthor, isReply, onDelete, onFlag, onLike, onDislike, onReply, busyId }) => {
  const isFlaggedByMe = comment.flaggedByMe
  const isLikedByMe = comment.likedByMe
  const isDislikedByMe = comment.dislikedByMe
  const authorName = comment.author?.displayName ?? 'Unknown'

  return (
    <div className={`flex gap-3 ${isReply ? 'pl-10 pt-3' : ''}`}>
      {comment.author?.avatar ? (
        <img src={comment.author.avatar} alt={authorName} className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-0.5" />
      ) : (
        <UserCircleIcon className={`text-gray-400 flex-shrink-0 mt-0.5 ${isReply ? 'w-6 h-6' : 'w-7 h-7'}`} />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{authorName}</span>
          <span className="text-xs text-gray-400 dark:text-gray-500">@{comment.author?.username ?? '?'}</span>
          <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
            {new Date(comment.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </span>
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300 break-words">{comment.text}</p>
        {comment.flagCount > 0 && (
          <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
            ⚠️ Flagged by {comment.flagCount} user{comment.flagCount > 1 ? 's' : ''}
          </p>
        )}
        <div className="flex items-center gap-3 mt-1.5">
          {/* Like / Dislike */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => currentUserId && onLike(comment._id)}
              disabled={!currentUserId || busyId === comment._id}
              title={isLikedByMe ? 'Unlike' : 'Like'}
              className={`flex items-center gap-0.5 text-xs transition-colors ${
                isLikedByMe ? 'text-green-600 dark:text-green-400' : 'text-gray-400 hover:text-green-600 dark:hover:text-green-400'
              } disabled:opacity-40`}
            >
              {isLikedByMe
                ? <HandThumbUpSolid className="w-3.5 h-3.5" />
                : <HandThumbUpIcon className="w-3.5 h-3.5" />}
              <span>{comment.likeCount > 0 ? comment.likeCount : ''}</span>
            </button>
            <button
              onClick={() => currentUserId && onDislike(comment._id)}
              disabled={!currentUserId || busyId === comment._id}
              title={isDislikedByMe ? 'Remove dislike' : 'Dislike'}
              className={`flex items-center gap-0.5 text-xs transition-colors ${
                isDislikedByMe ? 'text-red-500 dark:text-red-400' : 'text-gray-400 hover:text-red-500 dark:hover:text-red-400'
              } disabled:opacity-40`}
            >
              {isDislikedByMe
                ? <HandThumbDownSolid className="w-3.5 h-3.5" />
                : <HandThumbDownIcon className="w-3.5 h-3.5" />}
              <span>{comment.dislikeCount > 0 ? comment.dislikeCount : ''}</span>
            </button>
          </div>
          {/* Reply — only on top-level comments, only for signed-in users */}
          {!isReply && onReply && currentUserId && (
            <button
              onClick={() => onReply(comment._id, authorName)}
              className="text-xs text-gray-400 hover:text-primary-500 transition-colors"
            >
              Reply
            </button>
          )}
          {/* Flag — any signed-in user except comment's own author */}
          {currentUserId && currentUserId !== comment.author?._id && (
            <button
              onClick={() => onFlag(comment._id)}
              disabled={busyId === comment._id || isFlaggedByMe}
              title={isFlaggedByMe ? 'Already flagged' : 'Flag as abuse/spam'}
              className={`flex items-center gap-1 text-xs transition-colors ${
                isFlaggedByMe ? 'text-amber-500 cursor-default' : 'text-gray-400 hover:text-amber-500'
              }`}
            >
              {isFlaggedByMe ? <FlagSolid className="w-3.5 h-3.5" /> : <FlagIcon className="w-3.5 h-3.5" />}
              {isFlaggedByMe ? 'Flagged' : 'Flag'}
            </button>
          )}
          {/* Delete — the comment's own author, the branch author, or an admin */}
          {(isAuthor || (!!currentUserId && currentUserId === comment.author?._id)) && (
            <button
              onClick={() => onDelete(comment._id)}
              disabled={busyId === comment._id}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
              title="Delete comment"
            >
              <TrashIcon className="w-3.5 h-3.5" />
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ViewCommentsModal
// ---------------------------------------------------------------------------
export const ViewCommentsModal: React.FC<{
  paragraphIndex: number
  comments: ParagraphComment[]
  currentUserId?: string
  isAuthor: boolean
  onDelete: (commentId: string) => Promise<void>
  onFlag: (commentId: string) => Promise<void>
  onLike: (commentId: string) => Promise<void>
  onDislike: (commentId: string) => Promise<void>
  onClose: () => void
  onAddComment: (replyTo?: { id: string; authorName: string }) => void
}> = ({ paragraphIndex, comments, currentUserId, isAuthor, onDelete, onFlag, onLike, onDislike, onClose, onAddComment }) => {
  const [busyId, setBusyId] = useState<string | null>(null)
  const totalCount = comments.reduce((s, c) => s + 1 + c.replies.length, 0)

  const handleDelete = async (id: string) => {
    setBusyId(id)
    try { await onDelete(id) } finally { setBusyId(null) }
  }

  const handleFlag = async (id: string) => {
    setBusyId(id)
    try { await onFlag(id) } finally { setBusyId(null) }
  }

  const handleLike = async (id: string) => {
    setBusyId(id)
    try { await onLike(id) } finally { setBusyId(null) }
  }

  const handleDislike = async (id: string) => {
    setBusyId(id)
    try { await onDislike(id) } finally { setBusyId(null) }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg flex flex-col" style={{ maxHeight: '80vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <ChatBubbleLeftEllipsisIcon className="w-5 h-5 text-primary-500" />
            {totalCount} comment{totalCount !== 1 ? 's' : ''} — paragraph {paragraphIndex + 1}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable thread list */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4 divide-y divide-gray-100 dark:divide-gray-700/60">
          {comments.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">No comments yet. Be the first!</p>
          ) : (
            comments.map((c) => (
              <div key={c._id} className="pt-4 first:pt-0">
                <CommentRow
                  comment={c}
                  currentUserId={currentUserId}
                  isAuthor={isAuthor}
                  onDelete={handleDelete}
                  onFlag={handleFlag}
                  onLike={handleLike}
                  onDislike={handleDislike}
                  onReply={(id, name) => onAddComment({ id, authorName: name })}
                  busyId={busyId}
                />
                {c.replies.length > 0 && (
                  <div className="mt-3 border-l-2 border-gray-100 dark:border-gray-700 space-y-3">
                    {c.replies.map((r) => (
                      <CommentRow
                        key={r._id}
                        comment={r}
                        currentUserId={currentUserId}
                        isAuthor={isAuthor}
                        isReply
                        onDelete={handleDelete}
                        onFlag={handleFlag}
                        onLike={handleLike}
                        onDislike={handleDislike}
                        busyId={busyId}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer: add new top-level comment */}
        {currentUserId && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <button
              onClick={() => onAddComment(undefined)}
              className="w-full py-2.5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-500 dark:text-gray-400 hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              + Add a comment…
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
